// One pairs event (Paul, 31 Oct 2026): /events/:eventId
//   open      the lobby: who's entered, enter with a partner (or withdraw), countdown to the start
//   seating   the start: waiting for the entered pairs to arrive, then the first round is seated
//   playing   my table (EventTable), or the standings between rounds / when sitting out
//   finished  final standings;  cancelled  a notice
// Live data comes from Firestore listeners (eventFeed.js); moves and registration go through the
// partnerHub function. All wording is neutral structural labels for Paul to replace.
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { ph, testUid } from "../PartnerHub/phApi";
import { Avatar } from "../PartnerHub/phUi";
import EventTable from "./EventTable";
import { useEventFeed, fmtWhen, fmtClock, FORMAT_LABEL, SCORING_LABEL } from "./eventFeed";
import "../PartnerHub/PartnerHub.css";
import "./Events.css";

function useNow(ms = 1000) {
  const [, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN((x) => x + 1), ms); return () => clearInterval(t); }, [ms]);
  return Date.now();
}

function Facts({ ev }) {
  const totalBoards = ev.format === "mitchell" ? (ev.tables ? ev.tables * ev.boardsPerRound : `${ev.boardsPerRound} a round`) : ev.rounds * ev.boardsPerRound;
  return (
    <ul className="ev-facts">
      <li><b>{FORMAT_LABEL[ev.format] || ev.format}</b></li>
      <li><b>{SCORING_LABEL[ev.scoring] || ev.scoring}</b></li>
      <li><b>{ev.rounds}</b> rounds × <b>{ev.boardsPerRound}</b> {ev.boardsPerRound === 1 ? "board" : "boards"} ({totalBoards} boards)</li>
      <li><b>{ev.roundClockMin} min</b> a round</li>
      <li>{ev.membership === "both" ? "Both players members" : "One member in each pair"}</li>
    </ul>
  );
}

function Standings({ st, myPairId, ev, title = "Standings" }) {
  if (!st || !st.rows || !st.rows.length) return <section className="pth-card"><h2 className="pth-h2">{title}</h2><p className="pth-muted">Nothing scored yet.</p></section>;
  const mp = st.scoring === "mp";
  return (
    <section className="pth-card" aria-label={title}>
      <h2 className="pth-h2">{title}</h2>
      <p className="pth-muted pth-small">After round {st.round} · {SCORING_LABEL[st.scoring] || st.scoring} · {st.rows.length} pairs</p>
      <table className="ev-standings">
        <thead><tr><th>#</th><th>Pair</th><th className="num">Boards</th><th className="num">{mp ? "%" : "IMPs"}</th></tr></thead>
        <tbody>
          {st.rows.map((r) => (
            <tr key={r.pairId} className={r.pairId === myPairId ? "is-me" : ""}>
              <td>{r.rank}</td>
              <td>{r.names.join(" & ")}{r.status === "withdrawn" ? " (withdrawn)" : ""}</td>
              <td className="num">{r.boards}</td>
              <td className="num">{mp ? `${r.pct}%` : (r.score > 0 ? `+${r.score}` : r.score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PairList({ pairs, myPairId, showRank }) {
  const live = pairs.filter((p) => p.status !== "withdrawn");
  if (!live.length) return <p className="pth-muted">No pairs entered yet.</p>;
  return (
    <ul className="ev-pairs">
      {live.map((p) => (
        <li key={p.pairId} className={p.pairId === myPairId ? "is-me" : ""}>
          <span className="ev-dots" title="Here now"><i className={p.present[0] ? "is-on" : ""} /><i className={p.present[1] ? "is-on" : ""} /></span>
          <span>{p.names.join(" & ")}</span>
          {p.status === "absent" && <span className="ev-badge">Didn't arrive</span>}
          {showRank && p.rank && <span className="ev-badge">#{p.rank}</span>}
        </li>
      ))}
    </ul>
  );
}

// Enter with one of my partners (the partner hub's list), or go and add one first.
function Register({ eventId, onDone, setErr }) {
  const [partners, setPartners] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { ph("hub").then((d) => { setPartners(d.partners); if (d.partners[0]) setPick(d.partners[0].pairId); }).catch((e) => setErr(e.message)); }, [setErr]);
  if (!partners) return <p className="pth-muted">Loading your partners…</p>;
  if (!partners.length) {
    return (
      <div className="ev-register">
        <p className="pth-muted">You need a partner on your partners list to enter.</p>
        <Link className="pth-btn pth-btn--primary" to="/partner">Add a partner</Link>
      </div>
    );
  }
  return (
    <form className="ev-register" onSubmit={async (e) => {
      e.preventDefault();
      setBusy(true);
      try { await ph("evRegister", { eventId, pairId: pick }); onDone(); } catch (err) { setErr(err.message); }
      setBusy(false);
    }}>
      <label className="pth-label" htmlFor="ev-partner">Enter with</label>
      <select id="ev-partner" className="browser-default" value={pick} onChange={(e) => setPick(e.target.value)}>
        {partners.map((p) => <option key={p.pairId} value={p.pairId}>{p.name}{p.online ? " · online now" : ""}</option>)}
      </select>
      <div className="pth-actions">
        <button type="submit" className="pth-btn pth-btn--primary" disabled={busy || !pick}>{busy ? "Entering…" : "Enter the event"}</button>
        <Link className="pth-btn pth-btn--ghost" to="/partner">Someone else</Link>
      </div>
      <p className="pth-note">Both of you need to be on this page when the event starts. Your partner is told on screen that you've entered.</p>
    </form>
  );
}

function Director({ ev, admin, setErr }) {
  const [tables, setTables] = useState(null);
  const [open, setOpen] = useState(false);
  const run = async (op, extra = {}) => {
    try { await ph("evDirector", { eventId: ev.id, op, ...extra }); window.dispatchEvent(new Event("ev-refresh")); } catch (e) { setErr(e.message); }
  };
  const loadTables = useCallback(() => ph("evTables", { eventId: ev.id }).then((r) => setTables(r.tables)).catch((e) => setErr(e.message)), [ev.id, setErr]);
  useEffect(() => { if (open && ev.status === "playing") { loadTables(); const t = setInterval(loadTables, 8000); return () => clearInterval(t); } return undefined; }, [open, ev.status, ev.round, loadTables]);
  if (!admin) return null;
  return (
    <section className="ev-director">
      <h2 className="pth-h2">Director</h2>
      <div className="pth-actions">
        {(ev.status === "open" || ev.status === "seating") && <button type="button" className="pth-btn pth-btn--primary" onClick={() => run("startNow")}>Start now with whoever is here</button>}
        {ev.status === "playing" && <button type="button" className="pth-btn pth-btn--ghost" onClick={() => run("endRound")}>End this round now</button>}
        {["open", "seating", "playing"].includes(ev.status) && <button type="button" className="pth-btn pth-btn--quiet" onClick={() => { if (window.confirm("Cancel this event for everyone?")) run("cancel"); }}>Cancel the event</button>}
        <button type="button" className="pth-btn pth-btn--quiet" onClick={() => run("tick")}>Run the clock</button>
        {ev.status === "playing" && <button type="button" className="pth-btn pth-btn--quiet" onClick={() => setOpen((o) => !o)}>{open ? "Hide tables" : "Tables"}</button>}
      </div>
      <p className="pth-muted pth-small">id {ev.id} · status {ev.status} · round {ev.round} · {ev.pairCount || 0} pairs entered</p>
      {open && tables && (
        <table>
          <thead><tr><th>Table</th><th>N</th><th>E</th><th>S</th><th>W</th><th>Board</th><th>Status</th></tr></thead>
          <tbody>
            {tables.map((t) => (
              <tr key={t.id}>
                <td>{t.tableNo}</td>
                {["N", "E", "S", "W"].map((s) => (
                  <td key={s}>
                    {t.seats[s] === "bot" ? "Robot" : <>{t.names[s]}{!t.humans[s] ? " 🤖" : ""}{t.humans[s] && t.seats[s] !== "bot" && <> <button type="button" className="pth-btn pth-btn--quiet" onClick={() => run("robot", { tableId: t.id, seat: s })}>Robot</button></>}</>}
                  </td>
                ))}
                <td>{t.boards[t.boardIdx]} ({t.boardIdx + 1}/{t.boards.length})</td>
                <td>{t.status}{t.clockOut ? " · clock" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function EventPage({ match, uid, authReady }) {
  const eventId = match.params.eventId;
  const signedIn = !!uid || !!testUid();
  const feed = useEventFeed(eventId, uid || testUid());
  const { event: ev, pairs, standings, myPair, mySeat, view, admin, offset } = feed;
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");
  const [sending, setSending] = useState(false);
  const now = useNow(1000) + (offset || 0);

  const act = useCallback(async (action, args) => {
    setSending(true);
    try {
      return await ph(action, { eventId, ...args });
    } catch (e) {
      setToast(e.message);
      setTimeout(() => setToast(""), 4000);
      return null;
    } finally {
      setSending(false);
      feed.refresh();
    }
  }, [eventId, feed]);

  if (!authReady && !testUid()) return <div className="pth-root"><div className="pth-page"><p className="pth-muted">Loading…</p></div></div>;
  if (!signedIn) {
    return (
      <div className="pth-root"><div className="pth-page pth-narrow"><div className="pth-card pth-center">
        <p>Sign in to see this event.</p>
        <Link className="pth-btn pth-btn--primary" to="/login">Sign in</Link>
      </div></div></div>
    );
  }
  if (feed.err && !ev) {
    return (
      <div className="pth-root"><div className="pth-page pth-narrow"><div className="pth-card pth-center">
        <p className="pth-err" role="alert">{feed.err}</p>
        <Link className="pth-btn pth-btn--ghost" to="/events">All events</Link>
      </div></div></div>
    );
  }
  if (!ev) return <div className="pth-root"><div className="pth-page"><div className="pth-skeleton" aria-label="Loading" /></div></div>;

  const myPairId = myPair ? myPair.pairId : null;
  const head = (
    <Helmet><title>{ev.name} — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
  );
  const hero = (
    <div className="ev-hero">
      <div className="pth-grow">
        <Link className="pth-backText" to="/events">← Events</Link>
        <h1 className="pth-h1">{ev.name}</h1>
        <p className="pth-muted" style={{ margin: 0 }}>{fmtWhen(ev.startsAt)}</p>
        <Facts ev={ev} />
        {ev.blurb && <p className="pth-mt">{ev.blurb}</p>}
      </div>
    </div>
  );
  const errBox = (err || toast) && <p className="pth-err" role="alert">{err || toast}</p>;

  // ── at my table ──
  if (ev.status === "playing" && mySeat && mySeat.tableId && view) {
    return (
      <div className="pth-root">
        {head}
        <EventTable view={view} event={ev} act={act} sending={sending} offset={offset || 0} />
        {toast && <div className="pth-toast" role="alert">{toast}</div>}
        {ev.nextRoundAt > 0 && (
          <div className="pth-page pth-narrow">
            <section className="pth-card ev-between">
              <span className="pth-muted">Round {ev.round} is over everywhere</span>
              <b>{ev.round < ev.rounds ? `Round ${ev.round + 1} starts in ${fmtClock(ev.nextRoundAt - now)}` : `Final results in ${fmtClock(ev.nextRoundAt - now)}`}</b>
            </section>
            <Standings st={standings} myPairId={myPairId} ev={ev} />
          </div>
        )}
        {admin && <div className="pth-page pth-narrow"><Director ev={ev} admin={admin} setErr={setErr} /></div>}
      </div>
    );
  }

  // ── lobby, seating, between rounds, finished ──
  let body;
  if (ev.status === "open") {
    const left = ev.startsAt - now;
    body = (
      <>
        <section className="pth-card">
          <div className="ev-hero">
            <div className="pth-grow">
              <h2 className="pth-h2">{myPair ? `You're entered with ${pairs.find((p) => p.pairId === myPairId)?.names.join(" & ") || "your partner"}` : "Enter"}</h2>
              {myPair ? (
                <>
                  <p className="pth-muted">Be on this page with your partner at {fmtWhen(ev.startsAt)}. Seating starts then; pairs not here within {Math.round(ev.lateGraceSec / 60)} minutes miss out.</p>
                  <button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" disabled={sending} onClick={async () => { if (await act("evWithdraw", {})) setErr(""); }}>Withdraw</button>
                </>
              ) : <Register eventId={eventId} onDone={() => { setErr(""); feed.refresh(); }} setErr={setErr} />}
            </div>
            <div className="ev-count">
              <b>{left > 0 ? (left > 3600000 ? `${Math.floor(left / 3600000)}h ${Math.floor((left % 3600000) / 60000)}m` : fmtClock(left)) : "Starting"}</b>
              <span>{left > 0 ? "until the start" : "any moment"}</span>
            </div>
          </div>
        </section>
        <section className="pth-card">
          <h2 className="pth-h2">Entered · {pairs.filter((p) => p.status !== "withdrawn").length} pairs</h2>
          <PairList pairs={pairs} myPairId={myPairId} />
        </section>
      </>
    );
  } else if (ev.status === "seating") {
    body = (
      <section className="pth-card pth-center">
        <h2 className="pth-h2">Taking seats<span className="pth-ellipsis" /></h2>
        <p className="pth-muted">{ev.presentCount || 0} of {ev.pairCount || 0} pairs are here. Play starts when everyone has arrived, or at {fmtWhen(ev.startsAt + (ev.lateGraceSec || 0) * 1000)}.</p>
        {myPair ? <p>Stay on this page: your table opens here.</p> : <p className="pth-muted">You're not entered in this event.</p>}
        <PairList pairs={pairs} myPairId={myPairId} />
      </section>
    );
  } else if (ev.status === "playing") {
    body = (
      <>
        <section className="pth-card ev-between">
          {mySeat && mySeat.bye ? <><span className="pth-muted">Round {ev.round}</span><b>You sit this round out</b></>
            : myPair && myPair.status === "absent" ? <b>Your pair wasn't here at the start</b>
              : myPair ? <><span className="pth-muted">Round {ev.round}</span><b>Your table is opening<span className="pth-ellipsis" /></b></>
                : <><span className="pth-muted">Round {ev.round} of {ev.rounds}</span><b>In play</b></>}
          {ev.nextRoundAt > 0 && <p className="pth-muted pth-mt">{ev.round < ev.rounds ? `Round ${ev.round + 1} starts in ${fmtClock(ev.nextRoundAt - now)}` : `Final results in ${fmtClock(ev.nextRoundAt - now)}`}</p>}
          {!ev.nextRoundAt && ev.roundEndsAt > now && <p className="pth-muted pth-mt">Round ends in {fmtClock(ev.roundEndsAt - now)}</p>}
        </section>
        <Standings st={standings} myPairId={myPairId} ev={ev} />
      </>
    );
  } else if (ev.status === "finished") {
    body = <Standings st={standings} myPairId={myPairId} ev={ev} title="Final results" />;
  } else if (ev.status === "cancelled") {
    body = (
      <section className="pth-card pth-center">
        <h2 className="pth-h2">This event didn't run</h2>
        <p className="pth-muted">{ev.cancelledWhy === "too-few" ? `Not enough pairs were here at the start (${ev.presentCount || 0} of ${ev.minPairs} needed).` : "It was cancelled by the director."}</p>
      </section>
    );
  }

  return (
    <div className="pth-root">
      {head}
      <div className="pth-page">
        {hero}
        {errBox}
        {body}
        <Director ev={ev} admin={admin} setErr={setErr} />
      </div>
    </div>
  );
}

export default connect((state) => ({ uid: state.auth?.uid, authReady: state.auth?.authReady === true }))(EventPage);
